import React, { useEffect, useState } from "react";
import { CurrentCaseResponse, STATUS_LABELS } from "../types";
import { ProgressBar, StatusBadge, formatMoney, Spinner } from "../common/ui";
import { api } from "../api";

const STATUS_FLOW = ["SUBMITTED", "DOCUMENT_REVIEW", "CREDIT_REVIEW", "READY_FOR_BANK", "SENT_TO_BANK", "BANK_REVIEW", "APPROVED"];

export function Dashboard({
  data, onContinue, onRefresh,
}: {
  data: CurrentCaseResponse;
  onContinue: () => void;
  onRefresh: () => void;
}) {
  const { case: c, completeness, profile } = data;
  const isSubmitted = c.status !== "DRAFT" && c.status !== "INCOMPLETE";

  if (isSubmitted) {
    return <SubmittedView caseId={c.id} caseNumber={c.caseNumber} status={c.status} onRefresh={onRefresh} />;
  }

  return (
    <div className="wizard-body">
      <div className="card">
        <div className="card-title">درخواست تأمین مالی شما</div>
        <div className="card-subtitle">
          {profile.storeName || "پرونده شما"} — پرونده {c.caseNumber}
        </div>
        <div className="flex-between mb-0">
          <span style={{ fontWeight: 700 }}>تکمیل پرونده</span>
          <span style={{ fontWeight: 800, color: "var(--raymand-indigo-deep)" }}>{completeness.percent}٪</span>
        </div>
        <div className="mt-2" />
        <ProgressBar percent={completeness.percent} />
      </div>

      <div className="card">
        {completeness.sections.map((s) => (
          <div className="checklist-item" key={s.key}>
            <span className={`checklist-icon ${s.complete ? "ok" : "warn"}`}>{s.complete ? "✓" : "!"}</span>
            <div style={{ flex: 1 }}>
              <div>{s.label}</div>
              {!s.complete && s.detail && <div className="text-sm">{s.detail}</div>}
            </div>
          </div>
        ))}
        <div className="text-sm mt-2">موارد باقی‌مانده: {completeness.missingCount}</div>
      </div>

      {c.requestedAmount > 0 && (
        <div className="card">
          <div className="card-subtitle mb-0">مبلغ تسهیلات درخواستی</div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{formatMoney(c.requestedAmount)} ریال</div>
        </div>
      )}

      <button className="btn btn-primary" onClick={onContinue}>
        {completeness.percent === 0 ? "شروع تکمیل پرونده" : "ادامه تکمیل پرونده"}
      </button>
    </div>
  );
}

function SubmittedView({ caseId, caseNumber, status, onRefresh }: { caseId: string; caseNumber: string; status: string; onRefresh: () => void }) {
  const [history, setHistory] = useState<{ toStatus: string; createdAt: string; note?: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.caseStatus().then((r) => setHistory(r.history)).finally(() => setLoading(false));
  }, []);

  const currentIndex = STATUS_FLOW.indexOf(status);

  return (
    <div className="wizard-body">
      <div className="card" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36 }}>✅</div>
        <div className="card-title">پرونده شما با موفقیت ثبت شد</div>
        <div className="text-sm">شماره پرونده</div>
        <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 800, margin: "6px 0 14px" }}>{caseNumber}</div>
        <StatusBadge status={status} labels={STATUS_LABELS} />
      </div>

      <div className="card">
        <div className="card-title">مسیر بررسی پرونده</div>
        <ul className="timeline mt-3">
          {STATUS_FLOW.map((s, i) => (
            <li key={s}>
              <span className={`dot ${i <= currentIndex ? "" : "pending"}`}>{i <= currentIndex ? "✓" : i + 1}</span>
              <div>
                <div style={{ fontWeight: i === currentIndex ? 700 : 400 }}>{STATUS_LABELS[s]}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}><Spinner /></div>
      ) : (
        <div className="card">
          <div className="card-title">تاریخچه</div>
          {history.map((h, i) => (
            <div key={i} className="checklist-item">
              <span className="checklist-icon ok">•</span>
              <div>
                <div>{STATUS_LABELS[h.toStatus] || h.toStatus}</div>
                {h.note && <div className="text-sm">{h.note}</div>}
                <div className="text-sm">{new Date(h.createdAt).toLocaleString("fa-IR")}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="btn btn-secondary btn-block" onClick={onRefresh}>بروزرسانی وضعیت</button>
    </div>
  );
}
